import type { PropsWithChildren } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Flex, Text } from '@atlaskit/primitives/compiled';

import { fg } from '@confluence/feature-gating';

import { SectionMessage } from '../UpsellLooknFeel/SectionMessage';

export const i18n = defineMessages({
	title: {
		id: 'change-edition.share-and-restrict-dialog.restrictions.title',
		defaultMessage: 'Control who can view and edit content',
		description: 'Upsell message title for the Restrictions feature',
	},
	learnMoreLink: {
		id: 'change-edition.share-and-restrict-dialog.restrictions.learn-more-link',
		defaultMessage: 'Learn more',
		description:
			'The text of the hyperlink which takes the user to a page explaining the feature Restrictions',
	},
	body: {
		id: 'change-edition.share-and-restrict-dialog.restrictions.body',
		defaultMessage:
			'You’re on the Free plan. On paid plans, you can restrict content to specific people.',
		description: 'Description of the benefit of upgrading for the Restrictions feature',
	},
	openUpAccess: {
		id: 'change-edition.share-and-restrict-inline-message.open-up-access',
		defaultMessage: 'Open up access',
		description: 'The text of a action which brings up the open access dialog',
	},
});

const SAC_CONTENT_PERMS_MANAGEMENT_DOCUMENTATION =
	'https://support.atlassian.com/confluence-cloud/docs/manage-permissions-on-the-page-level/';

const TEMP_PERMS_MANAGEMENT_DOCUMENTATION =
	'https://confluence-permissions-help.atlassian.net/wiki/external/YzQ2ZTA3NzNlZjU0NDllZTliOTRiZTNjMTM0NDliMzU';

export const ChangeEditionSectionMessageShareAndRestrict = ({
	children,
	handleOnClick,
	customPadding,
}: ChangeEditionSectionMessageShareAndRestrictProps) => {
	const { formatMessage } = useIntl();
	const learnMoreLink = fg('confluence_frontend_unified_restrict_and_share_ga')
		? SAC_CONTENT_PERMS_MANAGEMENT_DOCUMENTATION
		: TEMP_PERMS_MANAGEMENT_DOCUMENTATION;

	return (
		<SectionMessage
			withGradient={false}
			title={formatMessage(i18n.title)}
			body={
				<Flex direction="column" gap="space.050">
					<Text color="color.text.subtle">{formatMessage(i18n.body)}</Text>
					{children}
				</Flex>
			}
			learnMoreLink={{
				href: learnMoreLink,
				ariaLabel: i18n.learnMoreLink,
				appearance: handleOnClick ? 'primary' : undefined,
			}}
			source="shareAndRestrictDialogUpsell"
			secondaryLink={
				handleOnClick
					? {
							messageDescriptor: i18n.openUpAccess,
							appearance: 'link',
							onClick: handleOnClick,
							actionSubjectId: 'openUpAccess',
						}
					: undefined
			}
			customPadding={customPadding}
		/>
	);
};

type ChangeEditionSectionMessageShareAndRestrictProps = PropsWithChildren<{
	handleOnClick?(): void;
	customPadding?: string;
}>;
